// src/pages/bookings/AdminBookingPage.tsx

import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiSearch, FiTrash2 } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import Input from "@/components/shared/ui/Input";
import TabBar from "@/components/shared/ui/TabBar";
import TablePagination from "@/components/shared/ui/TablePagination";

import {
  useAdminDeleteBookingMutation,
  useAdminBulkDeleteBookingsMutation,
} from "@/api/features/bookings/mutations";
import type { AdminBulkDeleteSkipped } from "@/api/features/bookings/types";

import DeleteBookingsDialog, {
  type DeleteTarget,
} from "./components/DeleteBookingsDialog";

import {
  useAdminBookingsPage,
  type BookingRow,
  type BookingTabKey,
  type AdminBookingsColumnFilters,
} from "./hooks/useAdminBookingsPage";

/* ============================================================================
   Small helpers
============================================================================ */

function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = parts.map((p, idx) => {
    const href = "/" + parts.slice(0, idx + 1).join("/");
    const label = p
      .split("-")
      .map((s) => s[0]?.toUpperCase() + s.slice(1))
      .join(" ");
    return { href, label };
  });

  return (
    <nav className="text-xs text-gray-500">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <Link to="/" className="hover:text-gray-700">
            Home
          </Link>
        </li>
        {crumbs.map((c, i) => (
          <li key={c.href} className="flex items-center gap-1">
            <span>/</span>
            {i < crumbs.length - 1 ? (
              <Link to={c.href} className="hover:text-gray-700">
                {c.label}
              </Link>
            ) : (
              <span className="text-gray-700">{c.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function GlobalSearch({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative">
      <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search bookings…"
        className="w-72 rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
      />
    </div>
  );
}

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function formatDateShort(iso?: string | null) {
  const s = (iso || "").trim();
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s; // supports YYYY-MM-DD
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

/* ============================================================================
   Table
============================================================================ */


function StatusPill({ status, isFlightPage }: { status: string; isFlightPage?: boolean }) {
  const s = String(status || "").toUpperCase();
  const label =
    isFlightPage && s === "CONFIRMED"
      ? "TICKETED"
      : isFlightPage && s === "WAITING_PAYMENT"
      ? "PAYMENT PENDING"
      : isFlightPage && s === "ON_HOLD"
      ? "HOLD"
      : s === "ON_HOLD"
      ? "On Hold"
      : s;

  const cls =
    s === "ON_HOLD"
      ? "bg-violet-50 text-violet-800 border-violet-200"
      : s === "WAITING_PAYMENT"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : s === "PARTIALLY_PAID"
      ? "bg-yellow-50 text-yellow-800 border-yellow-200"
      : s === "CONFIRMED"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : s === "DRAFT"
      ? "bg-slate-50 text-slate-700 border-slate-200"
      : "bg-gray-50 text-gray-700 border-gray-200"; // OTHER

  return (
    <span
      className={cn(
        "inline-flex rounded-full border px-2 py-0.5 text-xs font-medium",
        cls
      )}
    >
      {label}
    </span>
  );
}

function BookingsTable({
  rows,
  serialStart,
  onRowClick,
  isFlightPage,
  colFilters,
  setColFilters,
  selectedIds,
  onToggleRow,
  onToggleAll,
  onDeleteRow,
}: {
  rows: BookingRow[];
  serialStart: number;
  onRowClick: (id: number) => void;
  isFlightPage?: boolean;
  colFilters: AdminBookingsColumnFilters;
  setColFilters: Dispatch<SetStateAction<AdminBookingsColumnFilters>>;
  selectedIds: Set<number>;
  onToggleRow: (id: number) => void;
  onToggleAll: (checked: boolean) => void;
  onDeleteRow: (row: BookingRow) => void;
}) {
  const allOnPageSelected =
    rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const someOnPageSelected =
    rows.some((r) => selectedIds.has(r.id)) && !allOnPageSelected;

  return (
    // Square bottom: the pagination bar below completes this card.
    <div className="overflow-hidden rounded-t-2xl border border-gray-200 bg-white">
      <div className="overflow-auto">
        <table className="min-w-[1140px] w-full text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50 text-xs text-gray-600">
            <tr className="border-b border-gray-200">
              <th className="px-3 py-2 text-left font-medium">
                <input
                  type="checkbox"
                  aria-label="Select all bookings on this page"
                  checked={allOnPageSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someOnPageSelected;
                  }}
                  onChange={(e) => onToggleAll(e.target.checked)}
                  className="h-4 w-4 cursor-pointer rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                />
              </th>
              <th className="px-3 py-2 text-left font-medium">#</th>
              <th className="px-3 py-2 text-left font-medium">
                <div className="flex items-center gap-1.5">
                  <span>Booking Code</span>
                  <ColumnFilter label="Booking Code">
                    <Input
                      placeholder="Contains…"
                      value={colFilters.bookingCode || ""}
                      onChange={(e) =>
                        setColFilters((f) => ({ ...f, bookingCode: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </th>
              {isFlightPage ? (
                <th className="px-3 py-2 text-left font-medium">PNR</th>
              ) : null}
              <th className="px-3 py-2 text-left font-medium">
                <div className="flex items-center gap-1.5">
                  <span>Customer</span>
                  <ColumnFilter label="Customer">
                    <Input
                      placeholder="Name or email…"
                      value={colFilters.customer || ""}
                      onChange={(e) =>
                        setColFilters((f) => ({ ...f, customer: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </th>
              {!isFlightPage ? (
                <th className="px-3 py-2 text-left font-medium">Product</th>
              ) : null}
              <th className="px-3 py-2 text-left font-medium">Travel</th>
              <th className="px-3 py-2 text-left font-medium">Pax</th>
              <th className="px-3 py-2 text-left font-medium">Total</th>
              <th className="px-3 py-2 text-left font-medium">
                {isFlightPage ? "Amount Paid" : "Tour Amount Paid"}
              </th>
              <th className="px-3 py-2 text-left font-medium">Updated</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
              <th className="px-3 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={isFlightPage ? 12 : 13} className="px-3 py-10 text-center text-gray-500">
                  No bookings found.
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr
                  key={r.id}
                  className={cn(
                    "cursor-pointer border-b border-gray-100 hover:bg-sky-50/40",
                    selectedIds.has(r.id) && "bg-sky-50/60"
                  )}
                  onClick={() => onRowClick(r.id)}
                >
                  {/* Selection and row actions must not open the booking. */}
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select booking ${r.bookingCode || r.id}`}
                      checked={selectedIds.has(r.id)}
                      onChange={() => onToggleRow(r.id)}
                      className="h-4 w-4 cursor-pointer rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                    />
                  </td>

                  <td className="px-3 py-3 text-gray-500">
                    {serialStart + idx + 1}
                  </td>

                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold text-gray-900">
                        {r.bookingCode || `#${r.id}`}
                      </span>
                      <span className="text-xs text-gray-500">ID: {r.id}</span>
                    </div>
                  </td>

                  {isFlightPage ? (
                    <td className="px-3 py-3 text-gray-700">
                      {r.pnr || "—"}
                    </td>
                  ) : null}

                  <td className="px-3 py-3">
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">
                        {r.customerName || "—"}
                      </span>
                      <span className="text-xs text-gray-500">
                        {r.customerEmail || r.customerPhone || "—"}
                      </span>
                    </div>
                  </td>

                  {!isFlightPage ? (
                    <td className="px-3 py-3">
                      <span className="text-gray-900">{r.productLabel || "—"}</span>
                    </td>
                  ) : null}

                  <td className="px-3 py-3 text-gray-700">
                    <div className="flex flex-col">
                      <span>{formatDateShort(r.travelStartDate)}</span>
                      <span className="text-xs text-gray-500">
                        to {formatDateShort(r.travelEndDate)}
                      </span>
                    </div>
                  </td>

                  <td className="px-3 py-3 text-gray-700">{r.paxLabel}</td>

                  <td className="px-3 py-3 font-medium text-gray-900">
                    {r.totalLabel}
                  </td>

                  <td className="px-3 py-3 font-medium text-gray-900">
                    {r.paidTotalLabel}
                  </td>

                  <td className="px-3 py-3 text-gray-700">
                    {formatDateShort(r.updatedAt || r.createdAt)}
                  </td>

                  <td className="px-3 py-3">
                    <StatusPill status={r.status} isFlightPage={isFlightPage} />
                  </td>

                  <td
                    className="px-3 py-3 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => onDeleteRow(r)}
                      aria-label={`Delete booking ${r.bookingCode || r.id}`}
                      title="Delete booking"
                      className="inline-grid h-8 w-8 place-items-center rounded-md text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================================
   Main page
============================================================================ */

export default function AdminBookingPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isFlightPage = pathname.startsWith("/flight-bookings");
  const bookingBasePath = isFlightPage ? "/flight-bookings" : "/bookings";
  const pageTitle = isFlightPage ? "Flight Bookings" : "Tour Bookings";

  const {
    tab,
    setTab,

    q,
    setQ,

    colFilters,
    setColFilters,

    page,
    setPage,
    perPage,
    totalPages,
    totalItems,
    serialStart,

    rows,
    tabCounts,

    isLoading,
    isError,
    refetch,
  } = useAdminBookingsPage(10, isFlightPage ? "FLIGHT" : "TOUR");

  const onRowClick = (id: number) => {
    navigate(`${bookingBasePath}/${id}`);
  };

  /* ---- Delete (single + bulk) ---- */

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleteTargets, setDeleteTargets] = useState<DeleteTarget[] | null>(null);
  const [skipped, setSkipped] = useState<AdminBulkDeleteSkipped[] | null>(null);

  const deleteOne = useAdminDeleteBookingMutation();
  const deleteMany = useAdminBulkDeleteBookingsMutation();
  const isDeleting = deleteOne.isPending || deleteMany.isPending;

  const toRow = useCallback(
    (r: BookingRow): DeleteTarget => ({
      id: r.id,
      bookingCode: r.bookingCode || `#${r.id}`,
      status: r.status,
    }),
    []
  );

  const toggleRow = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Only affects the current page; selections on other pages are preserved.
  const toggleAll = useCallback(
    (checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const r of rows) {
          if (checked) next.add(r.id);
          else next.delete(r.id);
        }
        return next;
      });
    },
    [rows]
  );

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedIds.has(r.id)),
    [rows, selectedIds]
  );

  const closeDialog = useCallback(() => {
    setDeleteTargets(null);
    setSkipped(null);
  }, []);

  const confirmDelete = useCallback(
    async (reason: string) => {
      const targets = deleteTargets ?? [];
      if (!targets.length) return;

      try {
        if (targets.length === 1) {
          await deleteOne.mutateAsync({ id: targets[0].id, reason });
          setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(targets[0].id);
            return next;
          });
          closeDialog();
          return;
        }

        const result = await deleteMany.mutateAsync({
          bookingIds: targets.map((t) => t.id),
          reason,
        });

        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (const id of result.deleted) next.delete(id);
          return next;
        });

        // Keep the dialog open to report anything the server refused.
        if (result.skipped.length) setSkipped(result.skipped);
        else closeDialog();
      } catch {
        // Mutation state surfaces the failure; leave the dialog open so the
        // reason the admin typed is not lost.
      }
    },
    [deleteTargets, deleteOne, deleteMany, closeDialog]
  );

  const renderTabLabel = (k: BookingTabKey) => {
    if (isFlightPage) {
      if (k === "ALL") return "All";
      if (k === "CONFIRMED") return "Ticketed";
      if (k === "WAITING_PAYMENT") return "Waiting Payment";
      if (k === "ON_HOLD") return "On Hold";
      if (k === "PARTIALLY_PAID") return "Partially Paid";
      if (k === "DRAFT") return "Draft";
      return "Other";
    }
    if (k === "ALL") return "All";
    if (k === "WAITING_PAYMENT") return "Waiting Payment";
    if (k === "PARTIALLY_PAID") return "Partially Paid";
    if (k === "ON_HOLD") return "On Hold";
    if (k === "CONFIRMED") return "Confirmed";
    if (k === "DRAFT") return "Draft";
    return "Other";
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{pageTitle}</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <GlobalSearch value={q} onChange={setQ} />
        </div>
      </div>

      {/* Tabs */}
      <TabBar
        ariaLabel="Booking status"
        className="mb-4"
        activeKey={tab}
        onSelect={(key) => setTab(key as BookingTabKey)}
        items={(
          (isFlightPage
            ? ["ALL", "CONFIRMED", "PARTIALLY_PAID", "OTHER", "ON_HOLD", "WAITING_PAYMENT"]
            : [
                "ALL",
                "CONFIRMED",
                "DRAFT",
                "OTHER",
                "PARTIALLY_PAID",
                "ON_HOLD",
                "WAITING_PAYMENT",
              ]) as BookingTabKey[]
        ).map((k) => ({
          key: k,
          label: renderTabLabel(k),
          count: tabCounts?.[k] ?? 0,
        }))}
      />

      {/* Error state */}
      {isError && (
        <div className="mb-3 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>Failed to load bookings.</span>
          <button
            onClick={() => refetch()}
            className="rounded border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      {/* Bulk action bar — only present when something is selected */}
      {selectedIds.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2">
          <span className="text-sm text-sky-900">
            <span className="font-semibold">{selectedIds.size}</span> selected
          </span>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="rounded-lg border border-sky-300 bg-white px-3 py-1.5 text-sm text-sky-800 hover:bg-sky-100"
            >
              Clear
            </button>
            <button
              onClick={() => setDeleteTargets(selectedRows.map(toRow))}
              disabled={selectedRows.length === 0}
              className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:bg-red-300"
            >
              <FiTrash2 />
              Delete selected
            </button>
          </div>
        </div>
      )}

      {/* Table + loading overlay */}
      <div className="relative">
        <BookingsTable
          rows={rows}
          serialStart={serialStart}
          onRowClick={onRowClick}
          isFlightPage={isFlightPage}
          colFilters={colFilters}
          setColFilters={setColFilters}
          selectedIds={selectedIds}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
          onDeleteRow={(r) => setDeleteTargets([toRow(r)])}
        />

        {isLoading && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/60">
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs text-gray-600 shadow-sm">
              Loading bookings…
            </div>
          </div>
        )}
      </div>

      {/* Pagination — shared card foot, same as the other list screens */}
      <TablePagination
        page={page}
        pageSize={perPage}
        total={totalItems}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      <DeleteBookingsDialog
        open={deleteTargets != null}
        targets={deleteTargets ?? []}
        isPending={isDeleting}
        skipped={skipped}
        onCancel={closeDialog}
        onConfirm={confirmDelete}
        onDone={closeDialog}
      />
    </AdminLayout>
  );
}
